import { formatUtcDateTime, formatUtcMonthYear, utcStringToLocalDate } from '../utils/timezone'
import '../style/components/RecapTileGrid.css'

type RecapTileGridProps = {
  items: Array<{
    id: string
    title?: string
    date_created?: string
    type?: 'CALL' | 'CHAT'
  }>
  onSelect?: (id: string, type?: 'CALL' | 'CHAT') => void
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
}

function RecapTileGrid({ items, onSelect, selectable = false, selectedIds, onToggleSelect }: RecapTileGridProps) {
  const groups = items.reduce<
    Record<
      string,
      {
        label: string
        entries: RecapTileGridProps['items']
      }
    >
  >((acc, item) => {
    const monthLabel = formatUtcMonthYear(item.date_created)
    const key = monthLabel || 'no-date'
    if (!acc[key]) {
      acc[key] = { label: monthLabel, entries: [] }
    }
    acc[key].entries.push(item)
    return acc
  }, {})

  const ordered = Object.entries(groups).sort((a, b) => {
    const firstDate = a[1].entries[0]?.date_created
    const secondDate = b[1].entries[0]?.date_created
    const firstTime = utcStringToLocalDate(firstDate)?.getTime() ?? 0
    const secondTime = utcStringToLocalDate(secondDate)?.getTime() ?? 0
    return secondTime - firstTime
  })

  return (
    <div className="stack" style={{ animation: 'fadeUp 400ms ease' }}>
      {ordered.map(([key, group]) => (
        <div key={key} className="section">
          {group.label && <div className="section-heading">{group.label}</div>}
          <div className="tiles-grid">
            {group.entries.map((item) => {
              const { date, time } = formatUtcDateTime(item.date_created)
              const title =
                (item.title || '').replace(/^["']+|["']+$/g, '').trim() || 'Untitled conversation'
              const isSelected = selectable ? Boolean(selectedIds?.has(item.id)) : false
              return (
                <div
                  key={item.id}
                  className={`tile data-tile clickable ${item.type === 'CHAT' ? 'type-ai' : ''} ${isSelected ? 'selected' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect?.(item.id, item.type)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onSelect?.(item.id, item.type)
                    }
                  }}
                >
                  {selectable ? (
                    <button
                      type="button"
                      className={`tile-select ${isSelected ? 'selected' : ''}`}
                      aria-label={isSelected ? 'Deselect conversation' : 'Select conversation'}
                      aria-pressed={isSelected}
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleSelect?.(item.id)
                      }}
                    >
                      {isSelected ? '✓' : ''}
                    </button>
                  ) : null}
                  <div className="tile-meta">
                    <span className="tile-pill">{date}</span>
                    <span className="tile-pill muted">{time || '—'}</span>
                    {item.type === 'CHAT' && <span className="tile-pill chat-pill">Chat</span>}
                  </div>
                  <div className="tile-title" title={title}>
                    {title}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export default RecapTileGrid
